import React, { createContext } from 'react'
import { BaseContextState, BaseContextProvider } from '../BaseContext'
import IAdminRole from '../../interfaces/IAdminRole'
import {
  AdminRoleListRequest,
  AdminRoleFindRequest,
  AdminRoleCreateRequest,
  AdminRoleUpdateRequest,
  AdminRoleDeleteRequest
} from '../../services/api/interfaces/ApiRequest'
import {
  adminRoleList,
  adminRoleFind,
  adminRoleCreate,
  adminRoleUpdate,
  adminRoleDelete
} from '../../services/api'

interface AdminRoleContextState extends BaseContextState {
  roles: Array<IAdminRole> | null,
  permissions: Array<IAdminRole> | null,
  role: IAdminRole | null
}

interface AdminRoleContextData extends AdminRoleContextState {
  resetErrors: Function,
  requestAdminRoleList: (args: AdminRoleListRequest) => void,
  requestAdminRoleFind: (args: AdminRoleFindRequest) => void,
  requestAdminRoleCreate: (args: AdminRoleCreateRequest) => Promise<any>,
  requestAdminRoleUpdate: (args: AdminRoleUpdateRequest) => Promise<any>,
  requestAdminRoleDelete: (args: AdminRoleDeleteRequest) => Promise<any>,
}

// Create context
const { Provider, Consumer } = createContext({} as AdminRoleContextData)

// Export consumer
export const AdminRoleConsumer = Consumer

export class AdminRoleProvider extends BaseContextProvider {
  state: AdminRoleContextState = {
    roles: null,
    permissions: null,
    role: null
  }

  /**
   * Make api request for admin role list
   * 
   * @param {AdminRoleListRequest} payload 
   */
  requestAdminRoleList = (payload: AdminRoleListRequest) => {
    this.setState({
      fetching: true,
      error: null,
      permissions: null
    }, async () => {
      const response = await adminRoleList(payload)

      // Process our response
      this.processResponse(response, { 
        mappings: {
          origin: 'roles',
          destination: payload.role === 1 ? 'roles' : 'permissions'
        }
      })
    })
  }

  /**
   * Make api request for admin role finder
   * 
   * @param {AdminRoleFindRequest} payload 
   */
  requestAdminRoleFind = (payload: AdminRoleFindRequest) => {
    this.setState({
      fetching: true,
      error: null,
      role: null
    }, async () => {
      const response = await adminRoleFind(payload)

      // Process our response
      this.processResponse(response, { 
        mappings: {
          origin: 'role',
          destination: 'role'
        }
      })
    })
  }

  /**
   * Make api request for admin role create
   * 
   * @param {AdminRoleCreateRequest} payload 
   */
  requestAdminRoleCreate = (payload: AdminRoleCreateRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        role: null,
        error: null
      }, async () => {
        let response = await adminRoleCreate(payload)
        this.processResponse(response, {
          keepFetching: true
        })
  
        if (this.state.success) {
          await this.requestAdminRoleFind({
            name: payload.admin_auth_item.name
          })

          resolve(true)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for admin role delete
   * 
   * @param {AdminRoleUpdateRequest} payload 
   */
  requestAdminRoleUpdate = (payload: AdminRoleUpdateRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        let response = await adminRoleUpdate(payload)
        this.processResponse(response)
  
        if (this.state.success) {
          resolve(true)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for admin role delete
   * 
   * @param {AdminRoleDeleteRequest} payload 
   */
  requestAdminRoleDelete = (payload: AdminRoleDeleteRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        let response = await adminRoleDelete(payload)
        this.processResponse(response)
  
        if (this.state.success) {
          this.requestAdminRoleList({
            role: 1
          })

          resolve(true)
        } else {
          reject(this.state.error)
        }
      })
    })
  }
  
  render() {
    return (
      <Provider
        value={{
          // States
          ...this.state,

          // Dispatches
          resetErrors: this.resetErrors,
          requestAdminRoleList: this.requestAdminRoleList,
          requestAdminRoleFind: this.requestAdminRoleFind,
          requestAdminRoleCreate: this.requestAdminRoleCreate,
          requestAdminRoleUpdate: this.requestAdminRoleUpdate,
          requestAdminRoleDelete: this.requestAdminRoleDelete
        }}
      >
        {this.props.children}
      </Provider>
    )
  }
}
